import React from 'react';
import { 
  Filter, 
  Search, 
  RotateCcw, 
  Check, 
  Sparkles,
  HeartPulse,
  Flame,
  Droplet,
  Wind,
  Layers
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';

const TibbiFilter = ({
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  selectedMizaj,
  setSelectedMizaj,
  selectedAilments,
  toggleAilment,
  inStockOnly,
  setInStockOnly,
  onResetFilters,
  isMobileModal = false
}) => {
  const { lang, t } = useLanguage();
  const { categories, mizajList, ailments, products = [] } = useStore();

  // Helper functions to get accurate product counts
  const totalProductsCount = products.length;

  const getCategoryCount = (catId) => {
    return products.filter(p => p.category === catId).length;
  };

  const getMizajCount = (mizajId) => {
    if (mizajId === 'all') return totalProductsCount;
    return products.filter(p => p.mizaj === mizajId).length;
  };

  const getAilmentCount = (ailmentId) => {
    return products.filter(p => Array.isArray(p.ailments) && p.ailments.includes(ailmentId)).length;
  };

  return (
    <div className={isMobileModal ? 'space-y-5' : 'bg-white rounded-3xl p-5 sm:p-6 shadow-sm border border-gray-100 space-y-6'}>
      
      {/* Header (shown in desktop sidebar) */}
      {!isMobileModal && (
        <div className="flex items-center justify-between border-b border-gray-100 pb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-pansar-700" />
            <h3 className="font-bold text-gray-900 text-base font-urdu">
              طبی فلٹرز و مزاج رہنمائی
            </h3>
          </div>
          <button
            onClick={onResetFilters}
            className="text-xs text-gray-400 hover:text-pansar-700 font-urdu flex items-center gap-1 transition cursor-pointer"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>{t('resetFilters')}</span>
          </button>
        </div>
      )}

      {/* 1. Category Selection */}
      <div>
        <label className="block text-xs font-bold text-gray-700 font-urdu mb-2.5">
          {t('categories')}:
        </label>
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => setSelectedCategory('all')}
            className={`text-right px-3 py-2 rounded-xl text-xs font-urdu transition-all flex items-center justify-between group cursor-pointer ${
              selectedCategory === 'all'
                ? 'bg-pansar-800 text-white font-bold shadow-sm'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span className="flex-1">{t('allCategories')}</span>
            <div className="flex items-center gap-1.5">
              <span className={`text-[11px] font-sans font-bold px-1.5 py-0.5 rounded-md transition-colors ${
                selectedCategory === 'all' ? 'bg-white/20 text-white' : 'bg-gray-200/80 text-gray-600 group-hover:bg-gray-300/80'
              }`}>
                {totalProductsCount}
              </span>
              {selectedCategory === 'all' && <Check className="w-3.5 h-3.5 text-amber-300" />}
            </div>
          </button>

          {(categories || []).map(cat => {
            const count = getCategoryCount(cat.id);
            const isSelected = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                className={`text-right px-3 py-2 rounded-xl text-xs font-urdu transition-all flex items-center justify-between group cursor-pointer ${
                  isSelected
                    ? 'bg-pansar-800 text-white font-bold shadow-sm'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex-1">{lang === 'ur' ? cat.nameUrdu : cat.nameEn}</span>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[11px] font-sans font-bold px-1.5 py-0.5 rounded-md transition-colors ${
                    isSelected ? 'bg-white/20 text-white' : 'bg-gray-200/80 text-gray-600 group-hover:bg-gray-300/80'
                  }`}>
                    {count}
                  </span>
                  {isSelected && <Check className="w-3.5 h-3.5 text-amber-300" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Mizaj (Temperament) Filter */}
      <div className="border-t border-gray-100 pt-4">
        <label className="block text-xs font-bold text-gray-700 font-urdu mb-2.5 flex items-center gap-1.5">
          <Flame className="w-4 h-4 text-amber-500" />
          <span>{t('filterByMizaj')}</span>
        </label>
        <div className="grid grid-cols-1 gap-1.5">
          {(mizajList || []).map(mizaj => {
            const count = getMizajCount(mizaj.id);
            const isSelected = selectedMizaj === mizaj.id;
            return (
              <button
                key={mizaj.id}
                type="button"
                onClick={() => setSelectedMizaj(mizaj.id)}
                className={`text-right px-3 py-2 rounded-xl text-xs font-urdu transition-all flex items-center justify-between group cursor-pointer ${
                  isSelected
                    ? 'bg-amber-600 text-white font-bold shadow-sm'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex-1">{lang === 'ur' ? mizaj.nameUrdu : mizaj.nameEn}</span>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[11px] font-sans font-bold px-1.5 py-0.5 rounded-md transition-colors ${
                    isSelected ? 'bg-white/20 text-white' : 'bg-gray-200/80 text-gray-600 group-hover:bg-gray-300/80'
                  }`}>
                    {count}
                  </span>
                  {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Ailments / Diseases Filter */}
      <div className="border-t border-gray-100 pt-4">
        <label className="block text-xs font-bold text-gray-700 font-urdu mb-2.5 flex items-center gap-1.5">
          <HeartPulse className="w-4 h-4 text-rose-500" />
          <span>{t('filterByAilment')}</span>
        </label>
        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
          {(ailments || []).map(ailment => {
            const isChecked = selectedAilments.includes(ailment.id);
            const count = getAilmentCount(ailment.id);
            return (
              <label
                key={ailment.id}
                onClick={() => toggleAilment(ailment.id)}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer transition text-xs font-urdu ${
                  isChecked
                    ? 'bg-emerald-50 text-emerald-900 font-semibold border border-emerald-200'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-transparent'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => {}}
                  className="rounded text-pansar-700 focus:ring-pansar-500 w-3.5 h-3.5 cursor-pointer"
                />
                <span className="flex-1 select-none">
                  {lang === 'ur' ? ailment.nameUrdu : ailment.nameEn}
                </span>
                <span className={`text-[11px] font-sans font-bold px-1.5 py-0.5 rounded-md transition-colors ${
                  isChecked ? 'bg-emerald-200/70 text-emerald-900' : 'bg-gray-200/80 text-gray-600'
                }`}>
                  {count}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* 4. Stock Availability Toggle */}
      <div className="border-t border-gray-100 pt-4">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-xs font-bold text-gray-700 font-urdu">
            {t('inStockOnly')}
          </span>
          <input
            type="checkbox"
            checked={inStockOnly}
            onChange={(e) => setInStockOnly(e.target.checked)}
            className="w-4 h-4 text-pansar-700 rounded focus:ring-pansar-500 cursor-pointer"
          />
        </label>
      </div>

    </div>
  );
};

export default TibbiFilter;
